import React, { useState } from "react"
import {
  Smartphone, ShieldCheck, ShieldAlert, Lightbulb, Star, Layers, Wallet, Users2,
  Globe, Tag, TrendingUp, ThumbsUp, ThumbsDown, CheckCircle2, ListChecks,
  Images, MessageCircleWarning, ShoppingBag, Quote, AlertTriangle, Download,
  FileBarChart, Target, User, Gauge, Clock,
} from "lucide-react"
import { ReportCard, ReportSectionHeader, ReportStatTile, ReportEmptyState, ReportBadge } from "../components/ReportUI"
import { REPORT_COLORS } from "../components/reportTheme"
import MarkdownLite from "../components/MarkdownLite"
import AIEstimateCard, { estimationIsLocked } from "../components/AIEstimateCard"
import { toArray, formatNumbersInText, fit } from "../components/reportUtils"
import { PRIORITY_COLORS, getCategoryMeta, getPriorityKey, sortActions } from "../../../utils/actionsFeed"
import { isBlurredField, blurAware, badgeAware, BlurredInline, BlurredBlock, BlurredTagGroup, BlurredContentFade, BlurredGridPreview, BlurredStaticLines, partitionBlurred, PLACEHOLDER_WORD, PLACEHOLDER_SENTENCE, PLACEHOLDER_TAGS, safeLower } from "../components/BlurredField"

const fmtNum = (n, size, interactive = true) => {
  if (isBlurredField(n)) return <BlurredInline field={n} size={size} interactive={interactive} />
  if (n == null) return "—"
  const num = Number(n)
  if (Number.isNaN(num)) return String(n)
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`
  return num.toLocaleString()
}

const fmtCurrency = (n, size, interactive = true) => {
  if (isBlurredField(n)) return <BlurredInline field={n} size={size} interactive={interactive} />
  return n == null ? "—" : `$${fmtNum(n, undefined, interactive)}`
}

const fmtPrice = (price, interactive = true) => {
  if (isBlurredField(price)) return <BlurredInline field={price} interactive={interactive} />
  if (!price || typeof price !== "object") return "Free / N/A"
  const { currency, amount } = price
  if (isBlurredField(currency)) return <BlurredInline field={currency} interactive={interactive} />
  if (isBlurredField(amount)) return <BlurredInline field={amount} interactive={interactive} />
  if (!currency) return "Free / N/A"
  return currency === "USD" ? `$${amount}` : `${currency} ${amount}`
}

// The load section's `report.response` is the same structured JSON as the
// standalone AI Report (metrics/key_observations/recommendations/sentiment) —
// pull the parts not already duplicated elsewhere in this tab (its metrics/
// downloads/revenue/sentiment all overlap with sections already shown here
// from the structured analytics/sentiment data). A guest/lower-tier viewer
// gets individual fields (e.g. `explanation`) redacted as blurred-field
// descriptors rather than strings, same as everywhere else in this report —
// keep them raw here and let the render below handle it with blurAware.
const parseReportInsights = response => {
  const observations = []
  const recommendations = []
  if (!response || typeof response !== "object") return { observations, recommendations }

  for (const obs of response.key_observations || []) {
    observations.push({ title: obs.title, explanation: obs.explanation })
  }

  const rec = response.recommendations || {}
  recommendations.push(
    ...(rec.bug_fix_plan || []),
    ...(rec.keyword_tracking_monitoring || []),
    ...(rec.keyword_expansion || []).map(k => `${k.keyword} (${k.label})`),
  )

  return { observations, recommendations }
}

const severityTone = severity => {
  const s = safeLower(severity)
  if (s === "high") return "critical"
  if (s === "medium") return "warning"
  if (s === "low") return "good"
  return "neutral"
}

// Sentiment themes/complaints come back as either a plain string or an
// { theme|issue|bug, examples: [...] } object, depending on how much the
// source review data supported — read every shape rather than assuming one.
// The item itself, or any of its text sub-fields, may also be the admin's
// blurred-field descriptor instead of real content. Free text may also embed
// raw unformatted numbers straight from the source data — clean those too.
const themeLabel = (item, size = "sm", interactive = true) => {
  if (isBlurredField(item)) return <BlurredInline field={item} size={size} interactive={interactive} />
  if (typeof item === "string") return formatNumbersInText(item)
  const raw = item?.theme ?? item?.issue ?? item?.bug ?? item?.text
  if (isBlurredField(raw)) return <BlurredInline field={raw} size={size} interactive={interactive} />
  return raw ? formatNumbersInText(raw) : JSON.stringify(item)
}
const themeExamples = item => (item && typeof item === "object" && Array.isArray(item.examples) ? item.examples : [])

// True when this theme/complaint entry itself (or its embedded text field)
// is the admin's blurred descriptor rather than real content.
const isThemeLocked = item => isBlurredField(item) || isBlurredField(item?.theme ?? item?.issue ?? item?.bug ?? item?.text)

// Renders only the REAL entries — locked ones are swept out here rather than
// each getting its own dashed lock card. The Sentiment card below counts
// what's swept out across all three sibling ThemeLists (Top Positive/Negative
// Themes, Bug Complaints) and shows ONE shared unlock affordance for the
// whole card instead of one per list. If EVERY entry in this particular list
// is locked, that leaves nothing to render under its heading at all — a
// couple of static blurred lines fill that gap so it still reads as "gated",
// not "empty", ahead of the card's one shared Unlock below.
const ThemeList = ({ items }) => {
  const list = toArray(items)
  const visible = list.filter(item => !isThemeLocked(item))
  if (visible.length === 0 && list.length > 0) return <BlurredStaticLines lines={2} />
  return visible.map((item, i) => (
    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 6 }}>
      • {themeLabel(item)}
      {/* A locked example quote is just omitted — no inline lock badge —
          folded into the Sentiment card's one shared Unlock via
          lockedExampleCount below instead. */}
      {themeExamples(item).slice(0, 2).filter(ex => !isBlurredField(ex)).map((ex, j) => (
        <div key={j} style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginLeft: 12, marginTop: 2, fontStyle: "italic" }}>
          “{ex}”
        </div>
      ))}
    </div>
  ))
}

// Self-contained: renders every real item, then — if any were locked —
// exactly ONE trailing "Unlock N more" line for the whole list, instead of a
// separate dashed card per locked entry. Every call site of BulletList gets
// this fix for free.
const BulletList = ({ items }) => {
  const { visible, locked } = partitionBlurred(toArray(items))
  return (
    <>
      {visible.map((item, i) => (
        <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visible.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
          {themeLabel(item, "lg")}
        </div>
      ))}
      {locked.length > 0 && <BlurredContentFade items={[]} />}
    </>
  )
}

const VISIBLE_REVIEW_COUNT = 2
const VISIBLE_ACTION_COUNT = 2

export default function PublicAICMOTab({ loadSection, appName }) {
  const [showFullDescription, setShowFullDescription] = useState(false)

  if (!loadSection?.available) {
    return (
      <ReportEmptyState
        icon={Smartphone}
        title="AI CMO not available"
        description="This part of the report hasn't been generated for this app yet."
      />
    )
  }

  const data = loadSection.data || {}
  const info = isBlurredField(data.game_info) ? {} : data.game_info || {}
  const infoBlurred = isBlurredField(data.game_info) ? data.game_info : null
  const analytics = isBlurredField(info.analytics) ? {} : info.analytics || {}
  const analyticsBlurred = isBlurredField(info.analytics) ? info.analytics : null
  const dlEstimate = info.ai_download_estimation
  const revEstimate = info.ai_revenue_estimation
  const sentiment = isBlurredField(data.sentiment) ? null : data.sentiment
  const sentimentBlurred = isBlurredField(data.sentiment) ? data.sentiment : null
  const policy = isBlurredField(data.policy?.policy_report) ? null : data.policy?.policy_report
  const policyBlurred = isBlurredField(data.policy?.policy_report) ? data.policy.policy_report : null
  const competitors = toArray(data.similar_apps)
  const insights = isBlurredField(data.improvement_insights) ? null : data.improvement_insights
  const actionsFeed = isBlurredField(data.actions_feed) ? null : data.actions_feed
  const actionsFeedBlurred = isBlurredField(data.actions_feed) ? data.actions_feed : null

  const reportResponseBlurred = isBlurredField(data.report?.response) ? data.report.response : null
  const reportInsights = parseReportInsights(reportResponseBlurred ? null : data.report?.response)

  const complianceStatus = policy?.summary?.compliance_status
  const isCompliant = safeLower(complianceStatus) === "compliant"
  const confidenceRaw = policy?.summary?.confidence
  const confidence = typeof confidenceRaw === "number" ? Math.round(confidenceRaw * 100) : null

  const blockingIssues = toArray(policy?.blocking_issues)
  const recommendedImprovements = toArray(policy?.recommended_improvements)
  const storePageGaps = toArray(policy?.store_page_gaps)
  const submissionChecklist = Object.entries(policy?.submission_checklist || {})

  const curatedKeywords = toArray(info.keywords)
  const keywordStats = info.keyword_stats || {}
  const addOns = toArray(info.add_ons)
  const canonicalTags = toArray(info.canonical_tags)
  const reviews = toArray(info.reviews).slice().sort((a, b) => (b?.upvotes || 0) - (a?.upvotes || 0))
  // `decorative` renders inside BlurredContentFade's preview stack, which
  // already owns ONE "Unlock" affordance for the whole faded region — an
  // individually-locked review there shouldn't also show its own separate
  // "Locked content" heading/message/Unlock chip, or it reads as locked
  // twice. Decorative mode drops that chrome and just fills in plausible
  // placeholder text, same as a normal review's shape, letting the fade's
  // blur do the "obscured" work instead.
  const renderReview = (r, i, decorative = false) => {
    if (isBlurredField(r)) {
      if (decorative) {
        return (
          <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${REPORT_COLORS.cardBorder}` }}>
            <strong style={{ color: REPORT_COLORS.textPrimary, display: "block", marginBottom: 4 }}>{PLACEHOLDER_WORD}</strong>
            <div>{PLACEHOLDER_SENTENCE}</div>
          </div>
        )
      }
      return (
        <div key={i} style={{ marginBottom: 10 }}>
          <BlurredInline field={r} size="lg" />
        </div>
      )
    }
    return (
      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${REPORT_COLORS.cardBorder}` }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
          <strong style={{ color: REPORT_COLORS.textPrimary }}>{r?.title}</strong>
          {r?.upvotes != null && (
            <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <ThumbsUp size={11} /> {fmtNum(r.upvotes)}
            </span>
          )}
        </div>
        {r?.review != null && !isBlurredField(r.review) && <div>{r.review}</div>}
      </div>
    )
  }
  // A review whose title is locked is swept out entirely by the caller
  // before this ever runs (same "no orphan content" fix as Top Competitors/
  // Competitor Threats) — this only needs to account for a locked body on
  // an otherwise-visible review, omitted above rather than shown as its own
  // inline lock badge.
  const hasReviewSubFieldLock = r => isBlurredField(r?.review)
  const ratingIndex = isBlurredField(info.rating?.rating_index) ? null : info.rating?.rating_index
  const ratingIndexBlurred = isBlurredField(info.rating?.rating_index) ? info.rating.rating_index : null

  const genre = toArray(info.genre)
  const gameModes = toArray(info.game_modes)
  const supportedPlatforms = toArray(info.supported_platforms)
  const screenshots = toArray(info.screenshots)

  const breakdown = isBlurredField(sentiment?.sentiment_breakdown) ? {} : sentiment?.sentiment_breakdown || {}
  const breakdownBlurred = isBlurredField(sentiment?.sentiment_breakdown) ? sentiment.sentiment_breakdown : null
  const hasBreakdown = [breakdown.positive_percent, breakdown.neutral_percent, breakdown.negative_percent].some(v => typeof v === "number")

  const topPositiveThemes = toArray(sentiment?.top_positive_themes)
  const topNegativeThemes = toArray(sentiment?.top_negative_themes)
  const bugComplaints = toArray(sentiment?.bug_complaints)

  const howToImprove = toArray(insights?.how_to_improve)
  const keywordsToTarget = toArray(insights?.keywords_to_target)
  const competitorInsights = toArray(insights?.competitor_insights)
  const revenueTrendRaw = insights?.revenue_trend
  const revenueTrend = isBlurredField(revenueTrendRaw) || typeof revenueTrendRaw === "string" ? revenueTrendRaw : null
  const actionsFeedItems = toArray(actionsFeed?.feed)

  const websiteUrl = typeof info.website_url === "string" ? info.website_url : null

  return (
    <div>
      <ReportSectionHeader
        icon={Smartphone}
        eyebrow="AI CMO"
        // Non-interactive: the app identity card just below already shows
        // this same name with the ONE consolidated Unlock for the card —
        // this header copy would otherwise be a second button for the
        // exact same locked field.
        title={blurAware(info.name, v => v || appName, undefined, false)}
        // action={
        //   complianceStatus ? (
        //     isCompliant ? (
        //       <ReportBadge tone="good">Compliant</ReportBadge>
        //     ) : (
        //       badgeAware(complianceStatus, v => <ReportBadge tone="warning">{v}</ReportBadge>)
        //     )
        //   ) : null
        // }
      />

      {infoBlurred ? (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={infoBlurred} icon={Smartphone} />
        </div>
      ) : (() => {
        // This one card can independently lock name/rating/category/price/
        // publisher/age rating/website/platforms/tags/description — every
        // one of those below renders interactive={false}; this single check
        // decides whether the card needs its one shared Unlock at all.
        const identityLocked = !!(
          isBlurredField(info.name) || isBlurredField(info.rating?.average) || isBlurredField(info.category) ||
          isBlurredField(info.publisher) || isBlurredField(info.age_rating) || isBlurredField(info.website_url) ||
          isBlurredField(info.price) || isBlurredField(info.price?.currency) || isBlurredField(info.price?.amount) ||
          isBlurredField(info.description) ||
          genre.some(isBlurredField) || supportedPlatforms.some(isBlurredField) ||
          gameModes.some(isBlurredField) || canonicalTags.some(isBlurredField)
        )
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: info.description ? 16 : 0 }}>
              {info.image_url || info.icon || info.portrait ? (
                <img
                  src={info.image_url || info.icon || info.portrait}
                  alt={typeof info.name === "string" ? info.name : "App icon"}
                  style={{ width: "clamp(44px, 13vw, 56px)", height: "clamp(44px, 13vw, 56px)", borderRadius: 14, objectFit: "cover", flexShrink: 0 }}
                />
              ) : (
                <div
                  style={{
                    width: "clamp(44px, 13vw, 56px)",
                    height: "clamp(44px, 13vw, 56px)",
                    borderRadius: 14,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: REPORT_COLORS.accentSoft,
                  }}
                >
                  <Smartphone size={24} color={REPORT_COLORS.accent} />
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>{blurAware(info.name, v => v || appName, undefined, false)}</div>
                <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted, display: "flex", alignItems: "center", gap: 16, marginTop: 4, flexWrap: "wrap" }}>
                  {info.rating?.average != null && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Star size={12} color={REPORT_COLORS.warning} /> {blurAware(info.rating.average, v => Number(v).toFixed(2), undefined, false)}
                    </span>
                  )}
                  {(info.category || genre.length > 0) && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Layers size={12} /> {blurAware(info.category, v => v || "Games", undefined, false)}
                      {genre.length > 0 && (
                        <>
                          {" · "}
                          {genre.slice(0, 2).map((g, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && ", "}
                              {themeLabel(g, "sm", false)}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </span>
                  )}
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <Wallet size={12} /> {fmtPrice(info.price, false)}
                  </span>
                  {info.publisher && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <Users2 size={12} /> {blurAware(info.publisher, undefined, undefined, false)}
                    </span>
                  )}
                  {info.age_rating && (
                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <ShieldCheck size={12} /> {blurAware(info.age_rating, undefined, undefined, false)}
                    </span>
                  )}
                  {isBlurredField(info.website_url) ? (
                    <BlurredInline field={info.website_url} interactive={false} />
                  ) : websiteUrl ? (
                    <a href={websiteUrl} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 4, color: REPORT_COLORS.accent, textDecoration: "none" }}>
                      <Globe size={12} /> Website
                    </a>
                  ) : null}
                </div>
                {(gameModes.length > 0 || supportedPlatforms.length > 0) && (
                  <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 8 }}>
                    {supportedPlatforms.length > 0 && (
                      <span>
                        Supported: {supportedPlatforms.map((p, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && ", "}
                            {themeLabel(p, "sm", false)}
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                    {gameModes.length > 0 && (
                      <span>
                        {supportedPlatforms.length > 0 ? " · " : ""}
                        {gameModes.map((m, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && ", "}
                            {themeLabel(m, "sm", false)}
                          </React.Fragment>
                        ))}
                      </span>
                    )}
                  </div>
                )}
                {canonicalTags.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {canonicalTags.map((tag, i) => (
                      <React.Fragment key={i}>{badgeAware(tag, v => <ReportBadge tone="neutral">{themeLabel(v)}</ReportBadge>, undefined, false)}</React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isBlurredField(info.description) ? (
              <div style={{ marginTop: 16 }}>
                <BlurredBlock field={info.description} icon={Smartphone} hideCta />
              </div>
            ) : (
              info.description && (
                <div>
                  <MarkdownLite
                    text={info.description}
                    style={{ maxHeight: showFullDescription ? "none" : 96, overflow: "hidden" }}
                  />
                  <button
                    onClick={() => setShowFullDescription(v => !v)}
                    style={{ background: "none", border: "none", color: REPORT_COLORS.accent, fontSize: 12, fontWeight: 600, padding: "8px 0 0", cursor: "pointer" }}
                  >
                    {showFullDescription ? "Show less" : "Read full description"}
                  </button>
                </div>
              )
            )}

            {identityLocked && <BlurredContentFade items={[]} />}
          </ReportCard>
        )
      })()}

      {/* Top competitors — shown right after the description, above the metric cards */}
      {competitors.length > 0 && (() => {
        // A competitor whose name is locked has nothing meaningful left to
        // show as a card — dropped from the visible grid entirely, same
        // "no orphan content" fix as Blocking Issues/Platform Playbook
        // elsewhere. rating/reasons are secondary — a visible competitor
        // can still show those locked individually, but rather than an
        // inline lock badge on every affected card (a "wall of locks" when
        // many are gated), the field is just omitted from that card; both
        // cases fold into the same locked count, so the grid preview and
        // the one shared Unlock below both agree on how many are gated.
        const { visible: rawVisible, locked } = partitionBlurred(competitors)
        const visible = rawVisible.filter(c => !isBlurredField(c.name))
        const totalLocked = locked.length + (rawVisible.length - visible.length) +
          visible.filter(c => isBlurredField(c.rating) || isBlurredField(c.reasons?.[0])).length
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Users2 size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Top Competitors</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, ${fit(220)})`, gap: 10 }}>
              {visible.map((c, i) => (
                <a
                  key={c.item_id || i}
                  href={c.store_url || c.meta_url || c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                    padding: 10,
                    borderRadius: 10,
                    backgroundColor: REPORT_COLORS.cardBgRaised,
                    textDecoration: "none",
                    minWidth: 0,
                  }}
                >
                  {c.image_url && <img src={c.image_url} alt={c.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                      <span style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                      {c.rating != null && !isBlurredField(c.rating) && (
                        <span style={{ fontSize: 11, color: REPORT_COLORS.warning, display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                          <Star size={10} /> {Number(c.rating).toFixed(1)}
                        </span>
                      )}
                    </div>
                    {c.review_count != null && <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 2 }}>{fmtNum(c.review_count)} reviews</div>}
                    {c.reasons?.[0] && !isBlurredField(c.reasons[0]) && <div style={{ fontSize: 11, color: REPORT_COLORS.textSecondary, marginTop: 4 }}>{formatNumbersInText(c.reasons[0])}</div>}
                  </div>
                </a>
              ))}
              {totalLocked > 0 && (
                <BlurredGridPreview
                  count={Math.min(totalLocked, 3)}
                  height={72}
                  renderPlaceholder={() => (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 10, borderRadius: 10, backgroundColor: REPORT_COLORS.cardBgRaised, height: "100%", boxSizing: "border-box" }}>
                      <div style={{ width: 32, height: 32, borderRadius: 8, backgroundColor: REPORT_COLORS.cardBorder, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary, fontFamily: "monospace" }}>{PLACEHOLDER_WORD}</div>
                        <div style={{ fontSize: 11, color: REPORT_COLORS.textMuted, marginTop: 2 }}>{PLACEHOLDER_WORD} reviews</div>
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
            {totalLocked > 0 && (
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Core metrics */}
      {!infoBlurred && (
        analyticsBlurred ? (
          <div style={{ marginBottom: 16 }}>
            <BlurredBlock field={analyticsBlurred} icon={Wallet} />
          </div>
        ) : (() => {
          const metricsLocked = [analytics.ku, analytics.asr, analytics.ktr, analytics.aur, confidenceRaw].some(isBlurredField)
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(160)})`, gap: 12 }}>
                {!dlEstimate && <ReportStatTile label="Downloads" value={fmtNum(analytics.d, "stat")} />}
                {!revEstimate && <ReportStatTile label="Revenue" value={fmtCurrency(analytics.revenue, "stat")} />}
                <ReportStatTile label="Reviews" value={fmtNum(analytics.urv, "stat")} />
                {!isBlurredField(analytics.ku) && <ReportStatTile label="KU Score" value={Number(analytics.ku).toFixed(2)} />}
                {!isBlurredField(analytics.asr) && <ReportStatTile label="ASR" value={fmtNum(analytics.asr)} />}
                {!isBlurredField(analytics.ktr) && <ReportStatTile label="KTR" value={Number(analytics.ktr).toFixed(3)} />}
                {!isBlurredField(analytics.aur) && <ReportStatTile label="AUR" value={Number(analytics.aur).toFixed(2)} />}
                {!isBlurredField(confidenceRaw) && <ReportStatTile label="Compliance Confidence" value={confidence != null ? `${confidence}%` : "—"} />}
              </div>
              {metricsLocked && (
                <div style={{ marginTop: 16, textAlign: "center" }}>
                  <BlurredContentFade items={[]} wrap />
                </div>
              )}
            </div>
          )
        })()
      )}

      {/* AI estimates — Download and Revenue are one pair, not two separate
          sections: one shared ReportCard, split into two columns by a
          vertical divider, with exactly ONE small, centered Unlock for the
          whole pair (only when either half actually has locked content). */}
      {(dlEstimate || revEstimate) && (() => {
        const estimatesLocked = estimationIsLocked(dlEstimate) || estimationIsLocked(revEstimate)
        const hasBoth = !!dlEstimate && !!revEstimate
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {dlEstimate && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <AIEstimateCard type="downloads" estimation={dlEstimate} showUnlock={false} bare />
                </div>
              )}
              {revEstimate && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <AIEstimateCard type="revenue" estimation={revEstimate} showUnlock={false} bare />
                </div>
              )}
            </div>
            {estimatesLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} wrap />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Target keywords */}
      {curatedKeywords.length > 0 && (() => {
        const { visible: visibleKeywords, locked: lockedKeywords } = partitionBlurred(curatedKeywords)
        // A visible keyword's per-keyword download stat can itself be
        // locked while the keyword name stays visible (unlike a keyword
        // whose whole entry is gated) — that used to render as its own
        // small lock icon + blurred bar UNDER EVERY TILE, which reads as a
        // wall of individual locks when most/all keywords have it gated.
        // Dropped here instead (the tile just shows the name, nothing
        // else) and folded into the same ONE shared Unlock below as the
        // fully-locked keywords, so the whole card only ever offers one way
        // to unlock, never one per tile.
        const anyDownloadsLocked = visibleKeywords.some(kw => isBlurredField(keywordStats[kw]?.total_downloads))
        const renderKeywordTile = (kw, i) => {
          const dl = keywordStats[kw]?.total_downloads
          return (
            <div
              key={i}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                padding: "6px 12px",
                borderRadius: 8,
                backgroundColor: REPORT_COLORS.cardBgRaised,
                border: `1px solid ${REPORT_COLORS.cardBorder}`,
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.textPrimary, whiteSpace: "nowrap" }}>{kw}</span>
              {dl != null && !isBlurredField(dl) && (
                <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, display: "flex", alignItems: "center", gap: 4 }}>
                  <Download size={10} /> {fmtNum(dl)} downloads
                </span>
              )}
            </div>
          )
        }
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Tag size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Target Keywords ({curatedKeywords.length})</span>
            </div>
            {visibleKeywords.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: (lockedKeywords.length > 0 || anyDownloadsLocked) ? 14 : 0 }}>
                {visibleKeywords.map(renderKeywordTile)}
              </div>
            )}
            {(lockedKeywords.length > 0 || anyDownloadsLocked) && (
              // Whole tail (or just the per-keyword download stat) is
              // gated — faded, with ONE centered Unlock below, same idea as
              // Recent Reviews. `wrap` mode is used (not the default
              // stack-fade) because that mode lays the preview and CTA out
              // in normal document flow — the default mode overlays the
              // CTA with `position: absolute; inset: 0`, which needs a tall
              // multi-row stack to have room; a single short pill row is
              // barely taller than the button itself, so the overlay just
              // sits on top of the pills instead of below them. No preview
              // pills at all when only the download stat is locked — the
              // keyword names are already shown above, there's nothing
              // shaped like a tag left to fade in.
              <div style={{ textAlign: "center" }}>
                <BlurredContentFade
                  items={lockedKeywords.length > 0 ? lockedKeywords.slice(0, 6) : []}
                  renderItem={(_, i) => (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "6px 12px",
                        borderRadius: 8,
                        fontSize: 12,
                        fontWeight: 600,
                        color: REPORT_COLORS.textPrimary,
                        backgroundColor: REPORT_COLORS.cardBgRaised,
                        border: `1px solid ${REPORT_COLORS.cardBorder}`,
                      }}
                    >
                      {PLACEHOLDER_TAGS[i % PLACEHOLDER_TAGS.length]}
                    </span>
                  )}
                  wrap
                />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Sentiment */}
      {sentimentBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={sentimentBlurred} icon={ThumbsUp} />
        </div>
      )}
      {sentiment && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>{blurAware(sentiment.overall_sentiment, v => v || "User Sentiment", undefined, false)}</span>
            {sentiment.data_confidence && (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {badgeAware(sentiment.data_confidence, v => <ReportBadge tone="neutral">{v}</ReportBadge>, undefined, false)} confidence
              </span>
            )}
          </div>
          {sentiment.overall_summary && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: hasBreakdown || breakdownBlurred ? 12 : 0 }}>{blurAware(sentiment.overall_summary, formatNumbersInText, "lg", false)}</div>}

          {breakdownBlurred && (
            <div style={{ marginBottom: 10 }}>
              <BlurredInline field={breakdownBlurred} interactive={false} />
            </div>
          )}

          {hasBreakdown && (
            <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", gap: 2, marginBottom: 10 }}>
              {breakdown.positive_percent > 0 && <div style={{ flexBasis: `${breakdown.positive_percent}%`, backgroundColor: REPORT_COLORS.good }} />}
              {breakdown.neutral_percent > 0 && <div style={{ flexBasis: `${breakdown.neutral_percent}%`, backgroundColor: REPORT_COLORS.warning }} />}
              {breakdown.negative_percent > 0 && <div style={{ flexBasis: `${breakdown.negative_percent}%`, backgroundColor: REPORT_COLORS.critical }} />}
            </div>
          )}

          {(topPositiveThemes.length > 0 || topNegativeThemes.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(200)})`, gap: 12, marginTop: 12 }}>
              {topPositiveThemes.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.good, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <ThumbsUp size={12} /> Top Positive Themes
                  </div>
                  <ThemeList items={topPositiveThemes} />
                </div>
              )}
              {topNegativeThemes.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.critical, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                    <ThumbsDown size={12} /> Top Negative Themes
                  </div>
                  <ThemeList items={topNegativeThemes} />
                </div>
              )}
            </div>
          )}

          {bugComplaints.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${REPORT_COLORS.cardBorder}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: REPORT_COLORS.textPrimary, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <MessageCircleWarning size={12} color={REPORT_COLORS.warning} /> Bug Complaints
              </div>
              <ThemeList items={bugComplaints} />
            </div>
          )}

          {/* One shared unlock affordance for the whole card — Top Positive
              Themes, Top Negative Themes, and Bug Complaints are three sibling
              lists that can each have locked entries; without this they'd
              each render their own separate dashed lock card. */}
          {(() => {
            const visibleThemes = [topPositiveThemes, topNegativeThemes, bugComplaints].flat().filter(item => !isThemeLocked(item))
            const lockedThemeCount =
              [topPositiveThemes, topNegativeThemes, bugComplaints]
                .flat()
                .filter(isThemeLocked).length +
              visibleThemes.reduce((n, item) => n + themeExamples(item).slice(0, 2).filter(isBlurredField).length, 0) +
              (isBlurredField(sentiment.overall_sentiment) ? 1 : 0) +
              (isBlurredField(sentiment.data_confidence) ? 1 : 0) +
              (isBlurredField(sentiment.overall_summary) ? 1 : 0) +
              (breakdownBlurred ? 1 : 0)
            return lockedThemeCount > 0 ? (
              <div style={{ marginTop: 12 }}>
                <BlurredContentFade
                  items={[]}
                />
              </div>
            ) : null
          })()}
        </ReportCard>
      )}

      {/* Report insights — the Key Observations / Recommendations that used to live
          only in the standalone AI Reports tab, folded in here since everything else
          that tab showed (downloads/revenue estimates, metrics, sentiment) already
          has a richer home elsewhere in this tab. */}
      {reportResponseBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={reportResponseBlurred} icon={FileBarChart} title="Performance report" />
        </div>
      )}
      {(reportInsights.observations.length > 0 || reportInsights.recommendations.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(320)})`, gap: 12, marginBottom: 16 }}>
          {reportInsights.observations.length > 0 && (() => {
            // Same "sweep locked entries out, one shared Unlock full estimates
            // CTA at the bottom" pattern as Blocking Issues / Recommended
            // Improvements below — not a per-item inline lock chip. An
            // observation whose title is locked has nothing meaningful left
            // to show; one whose title is visible but explanation is locked
            // still shows its title, just without the explanation line.
            const visibleObs = reportInsights.observations.filter(o => !isBlurredField(o.title))
            const lockedObsCount = (reportInsights.observations.length - visibleObs.length) + visibleObs.filter(o => isBlurredField(o.explanation)).length
            return (
              <ReportCard>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <FileBarChart size={14} color={REPORT_COLORS.textMuted} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Key Observations</span>
                </div>
                {visibleObs.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  visibleObs.slice(0, 3).map((o, i, arr) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < arr.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                      <strong style={{ color: REPORT_COLORS.textPrimary }}>{formatNumbersInText(o.title)}</strong>
                      {o.explanation && !isBlurredField(o.explanation) && (
                        <div style={{ marginTop: 4 }}>{formatNumbersInText(o.explanation.slice(0, 100))}{o.explanation.length > 100 ? "…" : ""}</div>
                      )}
                    </div>
                  ))
                )}
                {lockedObsCount > 0 && (
                  <div style={{ marginTop: 4, textAlign: "center" }}>
                    <BlurredContentFade items={[]} />
                  </div>
                )}
              </ReportCard>
            )
          })()}
          {reportInsights.recommendations.length > 0 && (() => {
            const visibleRecs = reportInsights.recommendations.filter(r => !isBlurredField(r))
            const lockedRecsCount = reportInsights.recommendations.length - visibleRecs.length
            return (
              <ReportCard>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <Lightbulb size={14} color={REPORT_COLORS.warning} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Top Recommendations</span>
                </div>
                {visibleRecs.length === 0 ? (
                  <BlurredStaticLines lines={2} />
                ) : (
                  visibleRecs.slice(0, 4).map((r, i) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 6, display: "flex", gap: 8 }}>
                      <span style={{ color: REPORT_COLORS.accent, fontWeight: 700 }}>•</span>
                      {formatNumbersInText(r)}
                    </div>
                  ))
                )}
                {lockedRecsCount > 0 && (
                  <div style={{ marginTop: 4, textAlign: "center" }}>
                    <BlurredContentFade items={[]} />
                  </div>
                )}
              </ReportCard>
            )
          })()}
        </div>
      )}

      {/* Add-ons / monetization */}
      {addOns.length > 0 && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <ShoppingBag size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>In-App Purchases ({addOns.length})</span>
          </div>
          {(() => {
            // Only shown when the API actually blurs a value — hidden
            // add-ons are just dropped from the grid (no per-tile lock
            // chip), with the one standard Unlock below only if anything's
            // actually gated.
            const { visible, locked } = partitionBlurred(addOns)
            const hasSubFieldLock = a => isBlurredField(a?.name) || isBlurredField(a?.price)
            const anyLocked = locked.length > 0 || visible.some(hasSubFieldLock)
            return (
              <>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, ${fit(160)})`, gap: 10 }}>
                  {visible.map((addOn, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 8, backgroundColor: REPORT_COLORS.cardBgRaised, fontSize: 12 }}>
                      <span style={{ color: REPORT_COLORS.textSecondary }}>{!isBlurredField(addOn?.name) && addOn?.name}</span>
                      <span style={{ color: REPORT_COLORS.textPrimary, fontWeight: 600, fontFamily: "monospace" }}>{!isBlurredField(addOn?.price) && fmtPrice(addOn?.price, false)}</span>
                    </div>
                  ))}
                </div>
                {anyLocked && (
                  <div style={{ marginTop: 12, textAlign: "center" }}>
                    <BlurredContentFade items={[]} />
                  </div>
                )}
              </>
            )
          })()}
        </ReportCard>
      )}

      {/* Policy: blocking issues + recommended improvements */}
      {policyBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={policyBlurred} icon={ShieldAlert} />
        </div>
      )}
      {policy && (blockingIssues.length > 0 || recommendedImprovements.length > 0) && (() => {
        // One shared card (two columns, vertical divider on desktop) with
        // ONE Unlock below both — not two separately-gated cards each with
        // their own button. Locked issues/recommendations are swept out
        // entirely rather than shown as their own blurred entry.
        // An issue whose title is locked has nothing meaningful left to show
        // (a severity badge floating with no text next to it reads as a
        // bug) — treat it the same as a fully-locked item: dropped from the
        // visible list, counted toward the shared lock total instead.
        const { visible: rawVisibleIssues, locked: lockedIssues } = partitionBlurred(blockingIssues)
        const visibleIssues = rawVisibleIssues.filter(issue => !isBlurredField(issue?.title))
        const issuesLocked = lockedIssues.length + (rawVisibleIssues.length - visibleIssues.length)

        const { visible: rawVisibleRecs, locked: lockedRecs } = partitionBlurred(recommendedImprovements)
        const visibleRecs = rawVisibleRecs.filter(rec => !isBlurredField(rec?.title))
        const recsLocked = lockedRecs.length + (rawVisibleRecs.length - visibleRecs.length)

        const anyLocked = issuesLocked > 0 || recsLocked > 0
        const hasBoth = blockingIssues.length > 0 && recommendedImprovements.length > 0

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {blockingIssues.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    {isCompliant ? <ShieldCheck size={14} color={REPORT_COLORS.good} /> : <ShieldAlert size={14} color={REPORT_COLORS.warning} />}
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
                      Blocking Issues {blockingIssues.length ? `(${blockingIssues.length})` : ""}
                    </span>
                  </div>
                  {blockingIssues.length === 0 ? (
                    <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No blocking issues found.</div>
                  ) : visibleIssues.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleIssues.map((issue, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visibleIssues.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <strong style={{ color: REPORT_COLORS.textPrimary }}>{issue.title}</strong>
                          {issue?.severity && !isBlurredField(issue.severity) && <ReportBadge tone={severityTone(issue.severity)}>{issue.severity}</ReportBadge>}
                        </div>
                        {issue?.description && !isBlurredField(issue.description) && <div style={{ marginTop: 4 }}>{formatNumbersInText(issue.description)}</div>}
                        {issue?.required_action && !isBlurredField(issue.required_action) && <div style={{ marginTop: 4, color: REPORT_COLORS.textMuted, fontStyle: "italic" }}>🔧 {formatNumbersInText(issue.required_action)}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}

              {recommendedImprovements.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <Lightbulb size={14} color={REPORT_COLORS.warning} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
                      Recommended Improvements {recommendedImprovements.length ? `(${recommendedImprovements.length})` : ""}
                    </span>
                  </div>
                  {recommendedImprovements.length === 0 ? (
                    <div style={{ fontSize: 12, color: REPORT_COLORS.textMuted }}>No recommendations available.</div>
                  ) : visibleRecs.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleRecs.map((rec, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visibleRecs.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        <strong style={{ color: REPORT_COLORS.textPrimary }}>{rec.title}</strong>
                        {rec?.suggested_improvement && !isBlurredField(rec.suggested_improvement) && <div style={{ marginTop: 2 }}>{formatNumbersInText(rec.suggested_improvement)}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Policy: store page gaps + submission checklist — one shared card,
          one shared Unlock, same as Blocking Issues / Recommended
          Improvements above. */}
      {policy && (storePageGaps.length > 0 || submissionChecklist.length > 0) && (() => {
        // A gap whose field name is locked has no heading to show — same
        // "no orphan badge/label" fix as Blocking Issues above.
        const { visible: rawVisibleGaps, locked: lockedGaps } = partitionBlurred(storePageGaps)
        const visibleGaps = rawVisibleGaps.filter(gap => !isBlurredField(gap?.field))
        const gapsLocked = lockedGaps.length + (rawVisibleGaps.length - visibleGaps.length)

        const visibleChecklist = submissionChecklist.filter(([, val]) => !isBlurredField(val))
        const lockedChecklist = submissionChecklist.filter(([, val]) => isBlurredField(val))

        const anyLocked = gapsLocked > 0 || lockedChecklist.length > 0
        const hasBoth = storePageGaps.length > 0 && submissionChecklist.length > 0

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {storePageGaps.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 12 }}>
                    Store Page Gaps ({storePageGaps.length})
                  </div>
                  {storePageGaps.length > 0 && visibleGaps.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    visibleGaps.map((gap, i) => (
                      <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visibleGaps.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                        <strong style={{ color: REPORT_COLORS.textPrimary }}>{typeof gap.field === "string" ? gap.field.replace(/[_-]+/g, " ").replace(/^\w/, c => c.toUpperCase()) : gap.field}</strong>
                        {gap?.issue && !isBlurredField(gap.issue) && <div style={{ marginTop: 2 }}>{formatNumbersInText(gap.issue)}</div>}
                        {gap?.how_to_fix && !isBlurredField(gap.how_to_fix) && <div style={{ marginTop: 2, color: REPORT_COLORS.textMuted, fontStyle: "italic" }}>🔧 {formatNumbersInText(gap.how_to_fix)}</div>}
                      </div>
                    ))
                  )}
                </div>
              )}

              {submissionChecklist.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <CheckCircle2 size={14} color={REPORT_COLORS.good} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Submission Checklist</span>
                  </div>
                  {visibleChecklist.length === 0 ? (
                    <BlurredStaticLines lines={2} />
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, ${fit(140)})`, gap: 8 }}>
                      {visibleChecklist.map(([key, val]) => {
                        const provided = val === "provided"
                        const notApplicable = val === "not_applicable"
                        return (
                          <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: REPORT_COLORS.textSecondary }}>
                            {provided ? (
                              <CheckCircle2 size={12} color={REPORT_COLORS.good} />
                            ) : notApplicable ? (
                              <CheckCircle2 size={12} color={REPORT_COLORS.textMuted} />
                            ) : (
                              <ShieldAlert size={12} color={REPORT_COLORS.warning} />
                            )}
                            {key.replace(/_/g, " ")}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {/* Improvement insights — one shared card, one shared Unlock. */}
      {(howToImprove.length > 0 || revenueTrend) && (() => {
        const { visible: visibleImprovements, locked: lockedImprovements } = partitionBlurred(toArray(howToImprove))
        const revenueTrendLocked = isBlurredField(revenueTrend)
        const anyLocked = lockedImprovements.length > 0 || revenueTrendLocked
        const hasBoth = howToImprove.length > 0 && !!revenueTrend

        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {howToImprove.length > 0 && (
                <div style={{ flex: "1 1 240px", minWidth: 0, borderRight: hasBoth ? `1px solid ${REPORT_COLORS.cardBorder}` : "none", paddingRight: hasBoth ? 20 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <Lightbulb size={14} color={REPORT_COLORS.warning} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>How to Improve</span>
                  </div>
                  {visibleImprovements.map((item, i) => (
                    <div key={i} style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, paddingBottom: 8, borderBottom: i < visibleImprovements.length - 1 ? `1px solid ${REPORT_COLORS.cardBorder}` : "none" }}>
                      {themeLabel(item, "lg")}
                    </div>
                  ))}
                </div>
              )}
              {revenueTrend && !revenueTrendLocked && (
                <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <TrendingUp size={14} color={REPORT_COLORS.textMuted} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Revenue Trend</span>
                  </div>
                  <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary }}>{formatNumbersInText(revenueTrend)}</div>
                </div>
              )}
            </div>
            {anyLocked && (
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <BlurredContentFade items={[]} />
              </div>
            )}
          </ReportCard>
        )
      })()}

      {keywordsToTarget.length > 0 && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Tag size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Keywords to Target</span>
          </div>
          <BlurredTagGroup
            items={keywordsToTarget}
            tone="neutral"
            renderVisible={(kw, i) => <React.Fragment key={i}><ReportBadge tone="neutral">{themeLabel(kw)}</ReportBadge></React.Fragment>}
          />
        </ReportCard>
      )}

      {competitorInsights.length > 0 && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Users2 size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Competitor Insights</span>
          </div>
          <BulletList items={competitorInsights} />
        </ReportCard>
      )}

      {/* Actions feed */}
      {actionsFeedBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={actionsFeedBlurred} icon={ListChecks} />
        </div>
      )}
      {actionsFeedItems.length > 0 && (
        <ReportCard style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ListChecks size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Actions Feed</span>
            </div>
            {actionsFeed?.summary && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(() => {
                  const high = actionsFeedItems.filter(a => !isBlurredField(a) && a?.problem && safeLower(a.priority) === "high").length
                  return high > 0 ? <ReportBadge tone="serious">{high} high priority</ReportBadge> : null
                })()}
                {badgeAware(actionsFeed.summary.problems, v => <ReportBadge tone="critical">{v ?? 0} problems</ReportBadge>, undefined, false)}
                {badgeAware(actionsFeed.summary.advantages, v => <ReportBadge tone="good">{v ?? 0} advantages</ReportBadge>, undefined, false)}
              </div>
            )}
          </div>
          {(() => {
            // `decorative` renders inside BlurredContentFade's preview stack,
            // which already owns ONE "Unlock" affordance for the whole faded
            // region — an item there shouldn't also show its own separate
            // "Locked content" chrome. It just fills in plausible
            // placeholder text in the same shape.
            //
            // A REAL (visible) action can still have individually-locked
            // sub-fields (title/priority/description/steps/expected_impact)
            // even though the action itself isn't blurred — those render via
            // interactive={false} (plain blurred text, no button) rather than
            // their own clickable chip, so this section never shows more
            // than the one shared CTA computed below.
            const renderAction = (item, i, decorative = false, last = false) => {
              if (decorative) {
                return (
                  <div key={i} style={{ marginBottom: last ? 0 : 12, paddingBottom: last ? 0 : 12, borderBottom: last ? "none" : `1px solid ${REPORT_COLORS.cardBorder}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <CheckCircle2 size={13} color={REPORT_COLORS.textMuted} />
                      <strong style={{ fontSize: 13, color: REPORT_COLORS.textPrimary }}>{PLACEHOLDER_WORD}</strong>
                    </div>
                    <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary }}>{PLACEHOLDER_SENTENCE}</div>
                  </div>
                )
              }
              const category = getCategoryMeta(item.category)
              const evidence = toArray(item.evidence)
              const steps = toArray(item.solution?.steps)
              const solution = item.solution || {}
              const accent = item.problem ? PRIORITY_COLORS[getPriorityKey(item)] : PRIORITY_COLORS.advantage
              const metaParts = [
                solution.owner && { icon: User, value: solution.owner },
                solution.effort && { icon: Gauge, value: solution.effort, suffix: " effort" },
                solution.timeframe && { icon: Clock, value: solution.timeframe },
              ].filter(Boolean)
              return (
                <div key={i} style={{ marginBottom: last ? 0 : 12, paddingBottom: last ? 0 : 12, borderBottom: last ? "none" : `1px solid ${REPORT_COLORS.cardBorder}` }}>
                  <div style={{ borderLeft: `3px solid ${accent}`, paddingLeft: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {item.problem ? (
                          <AlertTriangle size={13} color={accent} />
                        ) : (
                          <CheckCircle2 size={13} color={REPORT_COLORS.good} />
                        )}
                        <strong style={{ fontSize: 13, color: REPORT_COLORS.textPrimary }}>{blurAware(item.title, undefined, undefined, false)}</strong>
                      </span>
                      <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {category && !isBlurredField(item.category) && (
                          <ReportBadge tone="neutral">
                            <category.icon size={11} /> {category.label}
                          </ReportBadge>
                        )}
                        {item.problem ? (
                          item.priority && (
                            isBlurredField(item.priority) ? <BlurredInline field={item.priority} interactive={false} /> : (
                              <ReportBadge tone={severityTone(item.priority)}>{item.priority} priority</ReportBadge>
                            )
                          )
                        ) : (
                          <ReportBadge tone="good">Strength</ReportBadge>
                        )}
                      </span>
                    </div>
                    {item.description && <div style={{ fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 8, lineHeight: 1.5 }}>{blurAware(item.description, formatNumbersInText, "lg", false)}</div>}

                    {evidence.length > 0 && (
                      <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 10, backgroundColor: REPORT_COLORS.cardBgRaised }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: REPORT_COLORS.textMuted, marginBottom: 6 }}>
                          {item.problem ? "Why it was flagged" : "What's working"}
                        </div>
                        {evidence.map((e, j) => (
                          <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: j === evidence.length - 1 ? 0 : 4 }}>
                            <Quote size={10} color={accent} style={{ flexShrink: 0, marginTop: 3 }} />
                            <span>{blurAware(e, formatNumbersInText, "lg", false)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {steps.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: REPORT_COLORS.textPrimary, marginBottom: 6 }}>
                          {item.problem ? "Action plan" : "How to leverage it"}
                        </div>
                        {steps.map((s, j) =>
                          isBlurredField(s) ? (
                            <div key={j} style={{ marginBottom: 6 }}>
                              <BlurredInline field={s} size="lg" interactive={false} />
                            </div>
                          ) : (
                            <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: REPORT_COLORS.textSecondary, marginBottom: 5 }}>
                              <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: accent, backgroundColor: `${accent}1f` }}>{j + 1}</span>
                              <span style={{ lineHeight: 1.5 }}>{themeLabel(s, "sm", false)}</span>
                            </div>
                          )
                        )}
                      </div>
                    )}

                    {metaParts.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11, color: REPORT_COLORS.textMuted, marginBottom: 8 }}>
                        {metaParts.map(({ icon: Icon, value, suffix }, j) => (
                          <span key={j} style={{ display: "inline-flex", alignItems: "center", gap: 4, textTransform: "capitalize" }}>
                            <Icon size={11} />
                            {isBlurredField(value) ? <BlurredInline field={value} interactive={false} /> : <>{value}{suffix || ""}</>}
                          </span>
                        ))}
                      </div>
                    )}

                    {(solution.expected_impact || solution.success_signal) && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {solution.expected_impact && (
                          <div style={{ flex: "1 1 200px", padding: "8px 10px", borderRadius: 10, backgroundColor: REPORT_COLORS.accentSoft }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: REPORT_COLORS.accent, marginBottom: 4 }}>
                              <TrendingUp size={11} /> Expected impact
                            </div>
                            <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary }}>{blurAware(solution.expected_impact, formatNumbersInText, "lg", false)}</div>
                          </div>
                        )}
                        {solution.success_signal && (
                          <div style={{ flex: "1 1 200px", padding: "8px 10px", borderRadius: 10, backgroundColor: REPORT_COLORS.goodSoft }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: REPORT_COLORS.good, marginBottom: 4 }}>
                              <Target size={11} /> Success signal
                            </div>
                            <div style={{ fontSize: 12, color: REPORT_COLORS.textPrimary }}>{blurAware(solution.success_signal, formatNumbersInText, "lg", false)}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            }

            // Locked items are moved to the tail before slicing, so a locked
            // action can never land in the "leading/visible" slot with its
            // own separate lock card — every lock in this section ends up
            // consolidated into the one shared CTA below.
            const { visible: realActions, locked: lockedActions } = partitionBlurred(sortActions(actionsFeedItems))
            const visibleActions = realActions.slice(0, VISIBLE_ACTION_COUNT)
            const remainingActions = [...realActions.slice(VISIBLE_ACTION_COUNT), ...lockedActions]
            const remainingLocked = lockedActions.length > 0

            // A visible action can itself be real but still carry a locked
            // sub-field (title/priority/description/step/impact) — those
            // need a way to unlock too, but must share the SAME one CTA
            // rather than adding a second. Only shown when there's no
            // trailing fully-locked tail already covering it.
            const hasLockedSubField = item =>
              isBlurredField(item.title) || isBlurredField(item.priority) ||
              toArray(item.solution?.steps).some(isBlurredField) || isBlurredField(item.solution?.expected_impact) ||
              isBlurredField(item.description) || toArray(item.evidence).some(isBlurredField) ||
              ["owner", "effort", "timeframe", "success_signal"].some(k => isBlurredField(item.solution?.[k]))
            const subFieldLockCount =
              visibleActions.filter(hasLockedSubField).length +
              (isBlurredField(actionsFeed?.summary?.problems) ? 1 : 0) +
              (isBlurredField(actionsFeed?.summary?.advantages) ? 1 : 0)

            return (
              <>
                {visibleActions.map((item, i) => renderAction(item, i, false, remainingActions.length === 0 && i === visibleActions.length - 1))}
                {remainingActions.length > 0 && (
                  remainingLocked ? (
                    visibleActions.length === 0 ? (
                      // Nothing real to show at all (every action is
                      // locked) — same plain static blur lines as
                      // everywhere else in the report, ahead of the one
                      // shared Unlock, instead of the fake decorative cards.
                      <>
                        <BlurredStaticLines lines={2} />
                        <div style={{ marginTop: 12 }}>
                          <BlurredContentFade items={[]} />
                        </div>
                      </>
                    ) : (
                      <BlurredContentFade
                        items={remainingActions}
                        renderItem={(item, i) => renderAction(item, i, true)}
                      />
                    )
                  ) : (
                    remainingActions.map((item, i) => renderAction(item, i, false, i === remainingActions.length - 1))
                  )
                )}
                {!remainingLocked && subFieldLockCount > 0 && (
                  <BlurredContentFade items={[]} />
                )}
              </>
            )
          })()}
        </ReportCard>
      )}

      {/* Rating breakdown — secondary detail, shown after the substantive data */}
      {ratingIndexBlurred && (
        <div style={{ marginBottom: 16 }}>
          <BlurredBlock field={ratingIndexBlurred} icon={Star} title="Rating Breakdown" />
        </div>
      )}
      {ratingIndex && (() => {
        const starLockCount = [5, 4, 3, 2, 1].filter(star => isBlurredField(ratingIndex[star])).length
        const totalRatingLocked = isBlurredField(info.rating?.total_rating)
        const totalLocked = starLockCount + (totalRatingLocked ? 1 : 0)
        return (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Star size={14} color={REPORT_COLORS.warning} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>
                Rating Breakdown {info.rating?.total_rating != null && <>({fmtNum(info.rating.total_rating, undefined, false)} ratings)</>}
              </span>
            </div>
            {[5, 4, 3, 2, 1].map(star => {
              const count = typeof ratingIndex[star] === "number" ? ratingIndex[star] : 0
              const countBlurred = isBlurredField(ratingIndex[star]) ? ratingIndex[star] : null
              const total = Object.values(ratingIndex).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0) || 1
              const pct = (count / total) * 100
              return (
                <div key={star} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, width: 32, flexShrink: 0 }}>{star}★</span>
                  <div style={{ flex: 1, height: 6, borderRadius: 4, backgroundColor: REPORT_COLORS.cardBgRaised, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", backgroundColor: REPORT_COLORS.warning }} />
                  </div>
                  <span style={{ fontSize: 11, color: REPORT_COLORS.textMuted, width: 48, flexShrink: 0, textAlign: "right" }}>
                    {countBlurred ? <BlurredInline field={countBlurred} interactive={false} /> : fmtNum(count)}
                  </span>
                </div>
              )
            })}
            {totalLocked > 0 && <BlurredContentFade items={[]} />}
          </ReportCard>
        )
      })()}

      {/* Screenshots — secondary media, shown after the substantive data.
          No Unlock affordance here at all: locked screenshots are just
          dropped rather than shown as a blurred/"+N more" tile — only the
          real, visible images render. */}
      {(() => {
        const visibleScreenshots = screenshots.filter(src => !isBlurredField(src))
        return visibleScreenshots.length > 0 && (
          <ReportCard style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Images size={14} color={REPORT_COLORS.textMuted} />
              <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Screenshots</span>
            </div>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingTop: 6, paddingBottom: 6 }}>
              {visibleScreenshots.map((src, i) => (
                <a key={i} href={src} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }}>
                  <img src={src} alt={`Screenshot ${i + 1}`} style={{ height: 120, borderRadius: 8, objectFit: "cover" }} />
                </a>
              ))}
            </div>
          </ReportCard>
        )
      })()}

      {/* Recent reviews — secondary content, shown last */}
      {reviews.length > 0 && (
        <ReportCard>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Quote size={14} color={REPORT_COLORS.textMuted} />
            <span style={{ fontSize: 13, fontWeight: 600, color: REPORT_COLORS.textPrimary }}>Recent Reviews ({reviews.length})</span>
          </div>
          {(() => {
            // Locked reviews are moved to the tail before slicing — same fix
            // as Actions Feed — so a highly-upvoted locked review can never
            // land in the leading slot with its own separate lock card. A
            // review whose title is locked has nothing meaningful left to
            // show as a card (same fix as Top Competitors/Competitor
            // Threats) — dropped here too, counted the same way as a fully
            // locked review rather than rendered with a blank/blurred title.
            const { visible: rawRealReviews, locked: lockedReviews } = partitionBlurred(reviews)
            const realReviews = rawRealReviews.filter(r => !isBlurredField(r?.title))
            const titleLockedCount = rawRealReviews.length - realReviews.length
            const visibleReviews = realReviews.slice(0, VISIBLE_REVIEW_COUNT)
            const remainingReviews = [...realReviews.slice(VISIBLE_REVIEW_COUNT), ...lockedReviews]
            // Only fade+CTA the tail when it's actually gated — a fully
            // entitled viewer (nothing locked back there) just sees the
            // rest plainly instead of hitting a fake paywall on their own
            // data.
            const remainingLocked = lockedReviews.length > 0
            // A visible review can still carry its own locked review text —
            // that needs a way to unlock too, sharing the SAME one CTA
            // rather than adding a second, so it's only shown when the tail
            // isn't already covering it.
            const subFieldLockCount = titleLockedCount + visibleReviews.filter(hasReviewSubFieldLock).length
            return (
              <>
                {visibleReviews.map((r, i) => renderReview(r, i))}
                {remainingReviews.length > 0 && (
                  remainingLocked ? (
                    <BlurredContentFade
                      items={remainingReviews}
                      renderItem={(r, i) => renderReview(r, i, true)}
                    />
                  ) : (
                    remainingReviews.map((r, i) => renderReview(r, i))
                  )
                )}
                {!remainingLocked && subFieldLockCount > 0 && (
                  <BlurredContentFade items={[]} />
                )}
              </>
            )
          })()}
        </ReportCard>
      )}
    </div>
  )
}
